import emitter from "./event-bus";
import createClient from "openapi-fetch";
import type { paths as apiPaths } from "../src/redair-api/api";
import { getCookie, parseCalculationUidCookie, deleteCookie } from "./cookieUtils";

// Firstly we check for an "anonymous" attribute on the "Login" button, which is rendered in Razor.
// If that attribute is not present, we then check for the "calculationUid" cookie,
// which is set when a user starts a calculation without being logged in.
// Finally, we check if the calculation has an associated projectUid.
// If it doesn't, we show a modal prompting the user to either save the calculation or forget the calculation and delete the cookie.
// Exception: if the user is currently on that very calculation (e.g. they logged in straight from it),
// it is assigned to their account automatically without prompting.

const calculatorMountAttributes = ["data-link-calculator", "data-flex-multi-calculator", "data-horizontal-calculator", "data-redair-easy-calculator"];

function getCurrentPageCalculationUid(): string | null {
    for (const attribute of calculatorMountAttributes) {
        const element = document.querySelector<HTMLElement>("[" + attribute + "]");
        if (element) {
            return element.getAttribute(attribute);
        }
    }

    return null;
}

function isUserLoggedIn(): boolean {
    const navSection = document.querySelector("nav") as HTMLElement;

    if (!navSection) {
        return false;
    }

    const anonymousElement = navSection.querySelector("[anonymous]");
    const isAnonymous = anonymousElement !== null;
    return !isAnonymous;
}

export async function catchAnonymousCalculation(): Promise<void> {
    const cookieValue = getCookie("calculationUid");
    if (!cookieValue) {
        return;
    }

    const cookieData = parseCalculationUidCookie(cookieValue);
    if (!cookieData) {
        return;
    }

    const { uid: calculationUid, createdAt } = cookieData;

    const userLoggedIn = isUserLoggedIn();

    if (!userLoggedIn) {
        return;
    }

    const sharedAppContainerAttribute = "data-shared-app-container";
    const sharedAppContainerWrapper = document.querySelector<HTMLElement>("[" + sharedAppContainerAttribute + "]");
    const lang = sharedAppContainerWrapper.getAttribute(sharedAppContainerAttribute);

    const client = createClient<apiPaths>({ baseUrl: "" });
    const { data, error } = await client.GET("/redair/api/calculation/by-uid/{uid}", {
        params: {
            path: {
                uid: calculationUid,
            },
        },
    });

    if (error) {
        console.error("Failed to fetch calculation:", error);
        return;
    }

    if (data && data.projectUid === null && getCurrentPageCalculationUid()?.toLowerCase() === calculationUid.toLowerCase()) {
        const { data: assignData, error: assignError } = await client.PUT("/redair/api/calculation/assign-to-member/{uid}", {
            params: {
                path: {
                    uid: calculationUid,
                },
            },
        });

        if (assignError) {
            console.error("Failed to assign calculation to member:", assignError);
            return;
        }

        deleteCookie("calculationUid");

        // Reload so the page renders with the newly created project
        if (assignData?.calculationUrl) {
            window.location.replace(assignData.calculationUrl);
        }
    } else if (data && data.projectUid === null) {
        window.RedAir.openModal("CatchCalculation", {
            calculationUid,
            createdAt: createdAt.toISOString(),
            lang: lang,
        });
    } else if (data && data.projectUid != null) {
        deleteCookie("calculationUid");
    }
}

emitter.on("modalmanager:ready", catchAnonymousCalculation);
